import { useEffect } from 'react';

import { captureMessage } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { HUBSPOT_COOKIE_KEY, IS_PROD } from '@cast/constants';

import { parseApiError } from '@cast/api-client';
import { apiClient } from '@cast/infrastructure-api';
import { useUser } from '@cast/infrastructure-auth';
import { HubspotObject } from '@cast/types';
import { getCookie } from '@cast/utils';

import { useHubspotContact } from 'core/hubspot';

type Payload = { email: string; pageName: string };

type Props = {
  pageName: string;
};

export const useCreateHubspotContactMutation = ({ pageName }: Props) => {
  const user = useUser();
  const { initContactState, setInitContactState } = useHubspotContact();

  const { mutate } = useMutation({
    mutationFn: ({ email, pageName }: Payload) => {
      const hubspotCookie = getCookie(HUBSPOT_COOKIE_KEY);
      return apiClient.hubspot.createHubspotContact({
        submittedAt: new Date().getTime(),
        fields: [
          { objectTypeId: HubspotObject.CONTACT, name: 'email', value: email },
        ],
        context: {
          hutk: hubspotCookie!,
          pageUri: window.document.URL,
          pageName,
        },
      });
    },
    onSuccess: () => {
      setInitContactState('success');
    },
    onError: (error, payload) => {
      setInitContactState('failed');

      captureMessage(
        `Hubspot: Unable to submit INITIAL signup form. Payload: ${JSON.stringify(
          payload
        )}; Error: ${parseApiError(error)}`
      );
    },
  });

  const enabled =
    IS_PROD &&
    initContactState !== 'success' &&
    (user?.firstLogin || initContactState === 'failed');

  useEffect(() => {
    if (enabled && user?.id && user?.email) {
      mutate({ email: user.email, pageName });
    }
  }, [user, user?.email, user?.id, enabled, pageName, mutate]);

  return { success: initContactState === 'success' };
};
